import React, { useEffect, useState } from "react";

import format from "date-fns/format";
import isValid from "date-fns/isValid";
import moment from "date-fns/format";
import differenceInDays from "date-fns/differenceInDays";

import { ModalApp } from "components/Modal";
import DateRangePicker from "components/SearchComponents/DateRangePicker";
import Button from "components/Button";
import { useTranslation } from "contexts/Safra/TranslationContext";

import { useApp } from "contexts/AppContext";
import { changeDatesHotelAction } from "contexts/reducers/hotels/actions";
import { isChannel } from "../../../../utils";
import { useGenialApp } from "../../../../contexts/GenialAppContext";
import {
    trackCalendarOpen,
    trackDateSelected,
    trackDateRangeComplete
} from "../../../../utils/analytics";

// Helper function to check if date is valid
const isValidDate = (date) => {
    return date && date instanceof Date && isValid(date);
};

import Icon from 'components/Safra/Icon';

export default function ReserveDate({
    placeholderStart,
    placeholderEnd,
    iconStart = <span style={{display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0px 2px', minWidth: '20px', minHeight: '20px'}}><Icon name="calendar" size={20} color="#4F4F4F" /></span>,
    iconEnd = <span style={{display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0px 2px', minWidth: '20px', minHeight: '20px'}}><Icon name="calendar" size={20} color="#4F4F4F" /></span>,
    onlyIda = false,
    travel = false,
    boxed = false,
    initialDates
}) {
    const { t } = useTranslation();

    const { hotelState, hotelDispatch } = !isChannel('genial') ? useApp() : useGenialApp()
    const { datesSearchHotel } = hotelState

    const [open, setOpen] = useState(false);
    const [dates, setDates] = useState(initialDates ?? { startDate: null, endDate: null })
    const [focused, setFocused] = useState([0, 0])
    const [disabled, setDisabled] = useState(false)

    useEffect(() => {
        if (initialDates === undefined) return;
        setDates(initialDates);
    }, [initialDates?.startDate, initialDates?.endDate]);

    useEffect(() => {
        const hasStart = dates?.startDate instanceof Date && !isNaN(dates.startDate.getTime());
        const hasEnd = dates?.endDate instanceof Date && !isNaN(dates.endDate.getTime());

        if (onlyIda) {
            setDisabled(!hasStart);
        } else {
            setDisabled(!hasStart || !hasEnd);
        }
    }, [dates.startDate, dates.endDate, onlyIda])

    const iconWrapperStyle = {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 2px',
        minWidth: '20px',
        minHeight: '20px'
    }

    const dateFieldLabelStyle = {
        fontFamily: 'var(--font-family-base)',
        fontSize: '12px',
        fontWeight: 400,
        color: '#4F4F4F',
        letterSpacing: '-0.8px',
        lineHeight: '100%'
    };

    const dateFieldValueStyle = {
        fontFamily: 'var(--font-family-base)',
        fontSize: '14px',
        fontWeight: 600,
        color: '#4F4F4F',
        letterSpacing: '-0.8px',
        lineHeight: '100%'
    };

    const dateFieldPlaceholderStyle = {
        fontFamily: 'var(--font-family-base)',
        fontSize: '14px',
        fontWeight: 400,
        color: '#4F4F4F',
        letterSpacing: '-0.8px',
        lineHeight: '100%'
    };

    const handleModal = () => {
        const willOpen = !open;

        /**
         * Prevent closing the modal without the required data filled in.
         */
        if (!willOpen) {
            const hasStart = dates?.startDate instanceof Date && !isNaN(dates.startDate.getTime());
            const hasEnd = dates?.endDate instanceof Date && !isNaN(dates.endDate.getTime());

            if (onlyIda && !hasStart) return;
            if (!onlyIda && (!hasStart || !hasEnd)) return;
        }

        setOpen(willOpen);

        // Dispara evento GA4 quando o calendário é aberto
        if (willOpen) {
            const productType = travel ? 'flight' : 'hotel';
            trackCalendarOpen(productType);
        }
    };

    const handleKeyDownOpenModal = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleModal();
        }
    }

    const validateDates = (dates) => {
        let start = dates.startDate != null && moment(dates.startDate, "dd/MM/yyyy");
        let end = dates.endDate != null && moment(dates.endDate, "dd/MM/yyyy");

        if (travel) {
            return start && end;
        }

        return start !== end;
    }

    const handleChangeDates = (data) => {
        const date = data[0];

        const formatData = {
            startDate: date?.startDate,
            endDate: date?.endDate,
        }

        const hadStartDateBefore = dates?.startDate != null && dates?.startDate !== undefined;
        const productType = travel ? 'flight' : 'hotel';

        // Dispara evento GA4 quando a data de início é selecionada
        if (formatData.startDate && !hadStartDateBefore) {
            const dateType = travel ? 'departure' : 'checkin';
            trackDateSelected(
                productType,
                dateType,
                format(formatData.startDate, "yyyy-MM-dd")
            );
        }

        if (travel && !onlyIda && !hadStartDateBefore && formatData.startDate && formatData.endDate &&
            formatData.startDate.getTime && formatData.endDate.getTime &&
            formatData.startDate.getTime() === formatData.endDate.getTime()
        ) {
            formatData.endDate = null;
        }

        const checkedOnlyIda = onlyIda || (document.getElementById && document.getElementById('chkIda') && document.getElementById('chkIda').checked)

        const validate = validateDates(formatData)

        if (!validate) {
            formatData.endDate = null
            setDates(formatData)

            if (checkedOnlyIda) {
                setFocused([0, 0])
            } else {
                setFocused([0, 1])
                return false
            }
        }

        // Dispara evento GA4 quando a data de retorno é selecionada
        if (formatData.endDate && dates?.endDate !== formatData.endDate) {
            const dateType = travel ? 'return' : 'checkout';
            trackDateSelected(
                productType,
                dateType,
                format(formatData.endDate, "yyyy-MM-dd")
            );

            // Dispara evento quando ambas as datas estão completas
            if (formatData.startDate && formatData.endDate) {
                const nights = differenceInDays(formatData.endDate, formatData.startDate);
                trackDateRangeComplete(
                    productType,
                    format(formatData.startDate, "yyyy-MM-dd"),
                    format(formatData.endDate, "yyyy-MM-dd"),
                    nights
                );
            }
        }

        const formatISO = (selectedDate) => {
            const year = selectedDate.getFullYear()
            const month = String(selectedDate.getMonth() + 1).padStart(2, '0')
            const day = String(selectedDate.getDate()).padStart(2, '0')
            return `${year}-${month}-${day}`
        }

        localStorage.setItem('from-date', formatISO(formatData.startDate));
        localStorage.setItem('from-date-formatted', formatData.startDate.toLocaleDateString("pt-BR", { year: "numeric", month: "2-digit", day: "2-digit" }))

        const chk = document.getElementById('chkIda')
        const checkedOnlyIdaFinal = checkedOnlyIda || (chk && chk.checked)

        if (checkedOnlyIdaFinal) {
            localStorage.setItem('to-date', '')
            localStorage.setItem('to_date', '')
            localStorage.setItem('to-date-formatted', '')
            formatData.endDate = null
        } else {
            localStorage.setItem('to-date', formatISO(formatData.endDate));
            localStorage.setItem('to_date', formatISO(formatData.endDate));
            localStorage.setItem('to-date-formatted', formatData.endDate.toLocaleDateString("pt-BR", { year: "numeric", month: "2-digit", day: "2-digit" }))
        }

        setFocused([0, 0])

        setDates(formatData)
    }

    const clearDateValidationError = (elementId) => {
        const element = document.getElementById(elementId);
        if (element) {
            const inputGroup = element.closest('.input-group');
            if (inputGroup) {
                inputGroup.classList.remove('validation-error');
                const errorMsg = inputGroup.parentNode.querySelector('.validation-error-message');
                if (errorMsg) {
                    errorMsg.remove();
                }
            }
        }
    }

    const handleConfirm = () => {
        hotelDispatch(changeDatesHotelAction([dates]))

        if (dates?.startDate) {
            clearDateValidationError('passCheckin');
        }
        if (dates?.endDate) {
            clearDateValidationError('passCheckout');
        }

        handleModal()
    }

    useEffect(() => {
        if (onlyIda) {
            if (dates?.endDate) {
                const newDates = { ...dates, endDate: null }
                setDates(newDates)
            }

            localStorage.setItem('to-date', '')
            localStorage.setItem('to_date', '')
            localStorage.setItem('to-date-formatted', '')

            const checkout = document.getElementById('passCheckout')
            if (checkout) checkout.value = ''
        }
    }, [onlyIda])

    const isBoxed = boxed;

    return (
        <>
            {isBoxed ? (
                <div className="content-custom" style={{ gap: 0 }}>
                    <div style={{ display: 'flex', width: '100%' }}>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                minHeight: '62px',
                                padding: '16px',
                                cursor: 'pointer',
                                flex: 1,
                                border: '1px solid #D8D8D8',
                                borderRadius: onlyIda ? '4px' : '4px 0 0 4px',
                            }}
                            onClick={handleModal}
                            role="button"
                            aria-label={`${placeholderStart}: ${isValidDate(dates?.startDate) ? format(dates?.startDate, "dd/MM/yyyy") : 'Selecione'}`}
                            aria-expanded={open}
                            aria-haspopup="dialog"
                            tabIndex={0}
                            onKeyDown={handleKeyDownOpenModal}
                        >
                            <span style={iconWrapperStyle}>
                                {iconStart}
                            </span>
                            {isValidDate(dates?.startDate) ? (
                                <div className="d-flex flex-column" style={{ gap: '4px' }}>
                                    <span style={dateFieldLabelStyle}>{placeholderStart}</span>
                                    <span id='passCheckin' style={dateFieldValueStyle}>
                                        {format(dates?.startDate, "dd/MM/yyyy")}
                                    </span>
                                </div>
                            ) : (
                                <span
                                    id='passCheckin'
                                    style={{
                                        ...dateFieldPlaceholderStyle,
                                        fontSize: '14px',
                                        color: '#4F4F4F',
                                        fontWeight: 400
                                    }}
                                >
                                    {placeholderStart}
                                </span>
                            )}
                        </div>

                        {!onlyIda && (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    minHeight: '62px',
                                    padding: '16px',
                                    cursor: 'pointer',
                                    flex: 1,
                                    border: '1px solid #D8D8D8',
                                    borderLeft: '0',
                                    borderRadius: '0 4px 4px 0',
                                }}
                                onClick={handleModal}
                                role="button"
                                aria-label={`${placeholderEnd}: ${isValidDate(dates?.endDate) ? format(dates?.endDate, "dd/MM/yyyy") : 'Selecione'}`}
                                aria-expanded={open}
                                aria-haspopup="dialog"
                                tabIndex={0}
                                onKeyDown={handleKeyDownOpenModal}
                            >
                                <span style={iconWrapperStyle}>
                                    {iconEnd}
                                </span>
                                {isValidDate(dates?.endDate) ? (
                                    <div className="d-flex flex-column" style={{ gap: '4px' }}>
                                        <span style={dateFieldLabelStyle}>{placeholderEnd}</span>
                                        <span id='passCheckout' style={dateFieldValueStyle}>
                                            {format(dates?.endDate, "dd/MM/yyyy")}
                                        </span>
                                    </div>
                                ) : (
                                    <span
                                        id='passCheckout'
                                        style={{
                                            ...dateFieldPlaceholderStyle,
                                            fontSize: '14px',
                                            color: '#4F4F4F',
                                            fontWeight: 400
                                        }}
                                    >
                                        {placeholderEnd}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <div className="content-custom">
                    <div className="row" style={{ alignItems: 'flex-end', gap: '32px' }}>
                        {onlyIda ? (
                            <div className="col">
                                <div
                                    className="d-flex align-items-center"
                                    style={{ borderBottom: '1px solid var(--text-color)', paddingBottom: '8px', cursor: 'pointer', height: '38px', gap: '12px' }}
                                    onClick={handleModal}
                                    role="button"
                                    aria-label={`${placeholderStart}: ${isValidDate(dates?.startDate) ? format(dates?.startDate, "dd/MM/yyyy") : 'Selecione'}`}
                                    aria-expanded={open}
                                    aria-haspopup="dialog"
                                    tabIndex={0}
                                    onKeyDown={handleKeyDownOpenModal}
                                >
                                    <span style={iconWrapperStyle}>
                                        {iconStart}
                                    </span>
                                    {isValidDate(dates?.startDate) ? (
                                        <div className="d-flex flex-column" style={{ gap: '4px' }}>
                                            <span style={dateFieldLabelStyle}>{placeholderStart}</span>
                                            <span
                                                id='passCheckin'
                                                style={dateFieldValueStyle}
                                            >
                                                {format(dates?.startDate, "dd/MM/yyyy")}
                                            </span>
                                        </div>
                                    ) : (
                                        <span
                                            id='passCheckin'
                                            style={dateFieldPlaceholderStyle}
                                        >
                                            {placeholderStart}
                                        </span>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="col">
                                    <div
                                        className="d-flex align-items-center"
                                        style={{ borderBottom: '1px solid var(--text-color)', paddingBottom: '8px', cursor: 'pointer', minHeight: '38px', gap: '12px' }}
                                        onClick={handleModal}
                                        role="button"
                                        aria-label={`${placeholderStart}: ${isValidDate(dates?.startDate) ? format(dates?.startDate, "dd/MM/yyyy") : t('safra.components.reserve_date.select')}`}
                                    aria-expanded={open}
                                    aria-haspopup="dialog"
                                    tabIndex={0}
                                    onKeyDown={handleKeyDownOpenModal}
                                >
                                    <span style={iconWrapperStyle}>
                                        {iconStart}
                                    </span>
                                    {isValidDate(dates?.startDate) ? (
                                        <div className="d-flex flex-column" style={{ gap: '4px' }}>
                                            <span style={dateFieldLabelStyle}>{placeholderStart}</span>
                                            <span
                                                id='passCheckin'
                                                style={dateFieldValueStyle}
                                            >
                                                {format(dates?.startDate, "dd/MM/yyyy")}
                                            </span>
                                        </div>
                                    ) : (
                                        <span
                                            id='passCheckin'
                                            style={dateFieldPlaceholderStyle}
                                        >
                                            {placeholderStart}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="col">
                                <div
                                    className="d-flex align-items-center"
                                    style={{ borderBottom: '1px solid var(--text-color)', paddingBottom: '8px', cursor: 'pointer', height: '38px', gap: '12px' }}
                                    onClick={handleModal}
                                    role="button"
                                    aria-label={`${placeholderEnd}: ${isValidDate(dates?.endDate) ? format(dates?.endDate, "dd/MM/yyyy") : t('safra.components.reserve_date.select')}`}
                                    aria-expanded={open}
                                    aria-haspopup="dialog"
                                    tabIndex={0}
                                    onKeyDown={handleKeyDownOpenModal}
                                >
                                    <span style={iconWrapperStyle}>
                                        {iconEnd}
                                    </span>
                                    {isValidDate(dates?.endDate) ? (
                                        <div className="d-flex flex-column" style={{ gap: '4px' }}>
                                            <span style={dateFieldLabelStyle}>{placeholderEnd}</span>
                                            <span
                                                id='passCheckout'
                                                style={dateFieldValueStyle}
                                            >
                                                {format(dates?.endDate, "dd/MM/yyyy")}
                                            </span>
                                        </div>
                                    ) : (
                                        <span
                                            id='passCheckout'
                                            style={dateFieldPlaceholderStyle}
                                        >
                                            {placeholderEnd}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
            )}

            <ModalApp show={open} handleModal={handleModal} label={t('safra.components.reserve_date.modal_title')} hideClose={disabled}>

                <div className="row shadow-sm mb-1 mt-4">
                    {onlyIda ? (
                        <div className="col-12 d-flex justify-content-center">
                            <div className={`d-flex flex-column gap-1 py-2 ${focused[1] === 0 && 'border-focused'}`} style={{ width: '50%' }}>
                                <span className="text-xs fw-bold" style={{ color: "var(--title-color)" }}>{t('safra.home.fields.departure')}</span>
                                <span className="text-sm" style={{ color: "var(--text-color)" }}>
                                    {dates?.startDate
                                        ? format(dates?.startDate, "dd/MM/yyyy") : '-'}
                                </span>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="col-6">
                                <div className={`d-flex flex-column gap-1 py-2 ${focused[1] === 0 && 'border-focused'}`}>
                                    <span className="text-xs fw-bold" style={{ color: "var(--title-color)" }}>{t('safra.home.fields.departure')}</span>
                                    <span className="text-sm" style={{ color: "var(--text-color)" }}>
                                        {dates?.startDate
                                            ? format(dates?.startDate, "dd/MM/yyyy") : '-'}
                                    </span>
                                </div>
                            </div>
                            <div className="col-6">
                                <div className={`d-flex flex-column gap-1 py-2 ${focused[1] === 1 && 'border-focused'}`}>
                                    <span className="text-xs fw-bold" style={{ color: "var(--title-color)" }}>{t('safra.home.fields.return')}</span>
                                    <span className="text-sm" style={{ color: "var(--text-color)" }}>{dates?.endDate
                                        ? format(dates?.endDate, "dd/MM/yyyy") : '-'}</span>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                <DateRangePicker
                    onChange={handleChangeDates}
                    dates={dates}
                    onlyIda={onlyIda}
                />

                <div className="mt-2 mb-4">
                    <Button
                        type="button"
                        width="200px"
                        onClick={handleConfirm}
                        disabled={disabled}
                        aria-label={t('safra.components.reserve_date.confirm_aria_label')}
                    >
                        {t('safra.components.reserve_date.confirm')}
                    </Button>
                </div>
            </ModalApp>
        </>
    )
}
